import React, { useEffect, useState } from 'react'
import { debounce } from 'lodash'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'

import { useSnackbar } from '../../../helpers/hooks/useSnackbar'
import api from '../../../api/api'

import Filters from './Filters'
import Review from './Review'
import {
  getPeriodTime,
  getPeriodOptions,
  getDefaultPlatforms,
  getDefaultRatingOptions,
} from './helpers'
import NoReviewsFound from './NoReviewsFound'

const MAX_REVIEWS = 10

const ReviewsTab = ({ buildingId }) => {
  const { t } = useTranslation(['organisation'])
  const [isLoading, setIsLoading] = useState(false)
  const [reviews, setReviews] = useState([])
  const { displaySnackbar } = useSnackbar()
  const [skip, setSkip] = useState(0)
  const [period, setPeriod] = useState(getPeriodOptions(t)[0])
  const [notReplied, setNotReplied] = useState(false)
  const [immediateResponse, setImmediateResponse] = useState(false)
  const [platforms, setPlatforms] = useState(getDefaultPlatforms(t))
  const [ratings, setRatings] = useState(getDefaultRatingOptions(t))
  const [searchValue, setSearchValue] = useState('')
  const [prevSearchValue, setPrevSearchValue] = useState('')
  const [total, setTotal] = useState(0)

  const filterParams = [immediateResponse, notReplied, period, platforms, ratings]

  const fetchReviews = async (query) => {
    try {
      setIsLoading(true)

      const { from, to } = getPeriodTime(period)
      const filter = {
        from,
        to,
        notReplied,
        immediateResponse,
        skip: query ? 0 : skip,
        limit: MAX_REVIEWS,
        search: query || searchValue,
      }

      const allRatingFilterSelected = ratings[0].checked
      const allPlatformsFilterSelected = platforms[0].checked

      filter.rating = ratings
        .filter((item) => item.checked && !item.isAll && !allRatingFilterSelected)
        .map((item) => item.label)

      filter.provider = platforms
        .filter((item) => item.checked && !item.isAll && !allPlatformsFilterSelected)
        .map((item) => item.value)

      const { data = { reviews: [], total: 0 } } = await api.getReputationReviews(
        buildingId,
        filter,
      )

      setTotal(data.total)
      if (query || skip === 0) {
        setReviews(data.reviews)
      } else {
        setReviews((prev) => [...prev, ...data.reviews])
      }
    } catch (error) {
      displaySnackbar(t('organisation:reputationManagement.errors.fetchReviewsFailed'), true)
      console.error(error)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (buildingId) {
      fetchReviews()
    }
  }, [buildingId])

  useEffect(() => {
    setSkip(0)
  }, [searchValue, ...filterParams])

  const debouncedFetch = debounce(fetchReviews, 800)

  useEffect(() => {
    if (prevSearchValue !== searchValue) {
      window.addEventListener('keydown', debouncedFetch(searchValue))
    }
    setPrevSearchValue(searchValue)
    return () => {
      window.removeEventListener('keydown', debouncedFetch(searchValue))
    }
  }, [searchValue])

  useEffect(() => {
    const anyRatingFilterChecked = ratings.filter((r) => r.checked).length > 0

    if (anyRatingFilterChecked) {
      setImmediateResponse(false)
    }
  }, [ratings])

  useEffect(() => {
    if (immediateResponse) {
      setRatings((prev) => prev.map((rating) => ({ ...rating, checked: false })))
    }
  }, [immediateResponse])

  return (
    <div className={`reputation-reviews-tab ${isLoading ? 'is-loading' : ''}`}>
      <Filters
        setPeriod={setPeriod}
        setNotReplied={setNotReplied}
        setImmediateResponse={setImmediateResponse}
        setPlatforms={setPlatforms}
        setRatings={setRatings}
        setSearchValue={setSearchValue}
        period={period}
        notReplied={notReplied}
        immediateResponse={immediateResponse}
        platforms={platforms}
        ratings={ratings}
        searchValue={searchValue}
      />
      <div className="reputation-reviews-tab__reviews">
        {reviews.length > 0 ? (
          <React.Fragment>
            {reviews.map((review) => (
              <Review key={review.id} review={review} refetchReviews={fetchReviews} />
            ))}
            {total > reviews.length && (
              <button className="button button-gray" onClick={() => setSkip(skip + MAX_REVIEWS)}>
                {t('organisation:reputationManagement.reviewsTab.loadMore')}
              </button>
            )}
          </React.Fragment>
        ) : (
          <NoReviewsFound />
        )}
      </div>
    </div>
  )
}

ReviewsTab.propTypes = {
  buildingId: PropTypes.number,
}

export default ReviewsTab
